import { db } from './Database';

export interface SynopsisData {
    projectName: string;
    projectDescription: string;
    overallDecision: 'pass' | 'fail' | 'pending';
    overallDecisionFailureNote: string;
    validStackingTest: boolean;
    reasonForInvalidStackingTest: string;
    failureModes: Record<string, FailureModeStatuses>;
    newFailureModes: NewFailureMode[];
}

interface FailureModeStatuses {
    preShipLean: string;
    postShipLean: string;
    preShipFTL: string;
    postShipFTL: string;
    shipTestWalk: string;
    stackTestLeanWeekly: string;
    stackTestCompression: string;
    damageToPrimaryPackage: string;
    damageToSecondaryPackage: string;
    damageToTertiaryPackage: string;
}

interface NewFailureMode {
    failureModeName: string;
    status: 'pass' | 'fail' | 'dataMissing';
}

export class SynopsisService {
    private static readonly weeks = [
        "Stack start(stacked+30m)",
        "Stack Week-1",
        "Stack Week-2",
        "Stack Week-3",
        "Stack Week-4 (unstacking+30M)"
    ];

    static async getSynopsisData(projectId: number): Promise<SynopsisData> {
        try {
            const project = await db.projects.get(projectId);
            if (!project) {
                throw new Error('Project not found');
            }

            // Get all pallets for the project with their related data
            const pallets = await db.pallets
                .where('projectId')
                .equals(projectId)
                .toArray();

            const failureModes: Record<string, FailureModeStatuses> = {};

            // Process each pallet
            for (const pallet of pallets) {
                // Get all related data for the pallet
                const lpnNumbers = await db.palletLpnNumbers
                    .where('palletId')
                    .equals(pallet.id!)
                    .toArray();

                const leans = await db.leans
                    .where('palletLpnNumberId')
                    .anyOf(lpnNumbers.map(lpn => lpn.id!))
                    .toArray();

                const ftlRows = await db.ftlRows
                    .where('palletLpnNumberId')
                    .anyOf(lpnNumbers.map(lpn => lpn.id!))
                    .toArray();

                const unitLoadPositions = await db.unitLoadPositions
                    .where('palletLpnNumberId')
                    .anyOf(lpnNumbers.map(lpn => lpn.id!))
                    .toArray();

                // Determine pass/fail status for each failure mode
                failureModes[pallet.variant] = {
                    preShipLean: this.determinePassFailLean(leans, 'preShip', project.target),
                    postShipLean: this.determinePassFailLean(leans, 'postShip', project.target),
                    preShipFTL: this.determinePassFailFTL(ftlRows, 'preShip', project),
                    postShipFTL: this.determinePassFailFTL(ftlRows, 'postShip', project),
                    shipTestWalk: this.determinePassFailShipWalk(unitLoadPositions),
                    stackTestLeanWeekly: this.determinePassFailLean(leans, 'postStack', project.target),
                    stackTestCompression: this.determinePassFailCompression(pallet),
                    damageToPrimaryPackage: 'dataMissing',
                    damageToSecondaryPackage: 'dataMissing',
                    damageToTertiaryPackage: 'dataMissing',
                };
            }

            // Get new failure modes
            const newFailureModes = await db.newFailureModes
                .where('projectId')
                .equals(projectId)
                .toArray();

            return {
                projectName: project.projectName,
                projectDescription: project.projectDescription,
                overallDecision: project.overallDecision || 'pending',
                overallDecisionFailureNote: project.overallDecisionFailureNote || '',
                validStackingTest: project.validStackingTest || false,
                reasonForInvalidStackingTest: project.reasonForInvalidStackingTest || '',
                failureModes,
                newFailureModes
            };
        } catch (error) {
            console.error('Error getting synopsis data:', error);
            throw error;
        }
    }

    static async updateSynopsisData(projectId: number, data: SynopsisData): Promise<void> {
        return await db.transaction('rw', [db.projects, db.newFailureModes], async () => {
            try {
                const timestamp = new Date().toISOString();

                // Update project details
                await db.projects.update(projectId, {
                    projectName: data.projectName,
                    projectDescription: data.projectDescription,
                    overallDecision: data.overallDecision,
                    overallDecisionFailureNote: data.overallDecisionFailureNote,
                    validStackingTest: data.validStackingTest,
                    reasonForInvalidStackingTest: data.reasonForInvalidStackingTest,
                    updatedAt: timestamp
                });

                // Delete existing failure modes and create new ones
                await db.newFailureModes
                    .where('projectId')
                    .equals(projectId)
                    .delete();

                await Promise.all(data.newFailureModes.map(mode =>
                    db.newFailureModes.add({
                        projectId,
                        failureModeName: mode.failureModeName,
                        status: mode.status,
                        createdAt: timestamp,
                        updatedAt: timestamp
                    })
                ));
            } catch (error) {
                console.error('Error updating synopsis data:', error);
                throw error;
            }
        });
    }

    // Helper methods for determining pass/fail status
    private static determinePassFailLean(leans: any[], variant: string, target: number): string {
        // Implementation based on your business logic
        return 'dataMissing';
    }

    private static determinePassFailFTL(ftlRows: any[], variant: string, project: any): string {
        // Implementation based on your business logic
        return 'dataMissing';
    }

    private static determinePassFailShipWalk(positions: any[]): string {
        // Implementation based on your business logic
        return 'dataMissing';
    }

    private static determinePassFailCompression(pallet: any): string {
        // Implementation based on your business logic
        return 'dataMissing';
    }
}
